import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Wand2, Loader2 } from "lucide-react";
import { useImprovePlaybookStep } from "@/features/playbook/api/useImprovePlaybookStep";
import ChatContext from "@/shared/models/ChatContext";
import { useImproveInput } from "../api/useImproveInput";

interface PromptImproverModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalPrompt: string;
  onAccept: (improvedPrompt: string) => void;
  playbookId?: string;
  stepId?: string;
  chatId?: string;
  chatContext?: ChatContext;
}

export function PromptImproverModal({
  isOpen,
  onClose,
  originalPrompt,
  onAccept,
  playbookId,
  stepId,
  chatId,
  chatContext,
}: PromptImproverModalProps) {
  const [improvement, setImprovement] = useState("");
  const [improvedPrompt, setImprovedPrompt] = useState("");

  const { improveInput, isLoading: isImproving } = useImproveInput();
  const { improvePlaybookStep, isLoading: isImprovingPlaybookStep } =
    useImprovePlaybookStep();

  const isProcessing = useMemo(
    () => isImproving || isImprovingPlaybookStep,
    [isImproving, isImprovingPlaybookStep],
  );

  useEffect(() => {
    if (isOpen && originalPrompt && !improvedPrompt) {
      handleImprove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, originalPrompt]);

  const handleImprove = async () => {
    try {
      if (playbookId && stepId) {
        const result = await improvePlaybookStep(
          playbookId,
          stepId,
          improvement,
        );
        if (result) {
          setImprovedPrompt(result);
        }
      } else {
        const result = await improveInput(
          originalPrompt,
          chatContext,
          improvement,
          chatId,
        );
        if (result) {
          setImprovedPrompt(result);
        }
      }
    } catch (error) {
      console.error("Failed to improve prompt:", error);
    }
  };

  const handleClose = () => {
    setImprovement("");
    setImprovedPrompt("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Improve Prompt
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Original Prompt */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              Original Prompt
            </label>
            <div
              className="p-3 bg-gray-50 rounded-lg text-gray-700 text-sm whitespace-pre-wrap"
              style={{ overflowWrap: "anywhere" }}
            >
              {originalPrompt}
            </div>
          </div>

          {/* Improved Version */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-900">
                Improved Version
              </label>
              {isImproving && (
                <div className="flex items-center text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </div>
              )}
            </div>
            {improvedPrompt && (
              <div
                className="p-3 bg-blue-50 rounded-lg text-blue-900 text-sm whitespace-pre-wrap max-h-[200px] overflow-y-auto"
                style={{ overflowWrap: "anywhere" }}
              >
                {improvedPrompt}
              </div>
            )}
          </div>

          {/* Improvement Direction */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-900">
              How would you like to additionaly improve the prompt?
            </label>
            <Textarea
              placeholder="Optional: Add specific aspects you'd like to additionaly improve..."
              value={improvement}
              onChange={(e) => setImprovement(e.target.value)}
              className="h-24"
            />
            <div className="flex justify-end mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleImprove}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wand2 className="h-4 w-4 mr-2" />
                )}
                Generate Improvement
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              Use Original
            </Button>
            <Button
              onClick={() => {
                if (improvedPrompt) {
                  onAccept(improvedPrompt);
                  handleClose();
                }
              }}
              disabled={!improvedPrompt || isProcessing}
            >
              Use Improved Version
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
